import React, { useState, useRef, useCallback, useEffect } from "react";
import { ImageFileUpload, postRequest } from "../Utility/Utility";
import "./VoxPlayer.css";

const STATEMENTS = [
  "The quick brown fox jumps over the lazy dog.",
  "A journey of a thousand miles begins with a single step.",
  "Every cloud has a silver lining.",
  "Time flies when you are having fun.",
];

const STATE = {
  IDLE: "idle",
  RECORDING: "recording",
  REVIEW: "review",
  VERIFYING: "verifying",
  FAILED: "failed",
  SUCCESS: "success",
};

const formatTime = (s) =>
  `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

function useRecorder() {
  const mr = useRef(null),
    chunks = useRef([]),
    stream = useRef(null);
  const [rec, setRec] = useState(false),
    [blob, setBlob] = useState(null),
    [url, setUrl] = useState(null),
    [err, setErr] = useState(null);

  const start = useCallback(async () => {
    try {
      setErr(null);
      chunks.current = [];
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const m = new MediaRecorder(s);
      mr.current = m;

      m.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      m.onstop = () => {
        const b = new Blob(chunks.current, { type: "audio/webm" });
        setBlob(b);
        setUrl(URL.createObjectURL(b));
        s.getTracks().forEach((t) => t.stop());
      };

      m.start(100);
      setRec(true);
    } catch (e) {
      setErr(
        e.name === "NotAllowedError" ? "Microphone access denied" : e.message,
      );
    }
  }, []);

  const stop = useCallback(() => {
    if (mr.current && rec) {
      mr.current.stop();
      setRec(false);
    }
  }, [rec]);

  const reset = useCallback(() => {
    if (url) URL.revokeObjectURL(url);
    setBlob(null);
    setUrl(null);
    setErr(null);
    chunks.current = [];
  }, [url]);

  return { rec, blob, url, err, start, stop, reset };
}

export default function VoxPlayer({ userData }) {
  const [state, setState] = useState(STATE.IDLE);
  const [stmt, setStmt] = useState(
    STATEMENTS[Math.floor(Math.random() * STATEMENTS.length)],
  );
  const [status, setStatus] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const { rec, blob, url, err, start, stop, reset } = useRecorder();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);

  const params = new URLSearchParams(window.location.search);

  const voicePlayerId = params.get("uid")?.replace(/"/g, "") || "";

  console.log("VOICE PLAYER ID FROM URL:", voicePlayerId);
  console.log("FULL URL:", window.location.href);
  console.log("QUERY STRING:", window.location.search);



  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (rec) {
      setState(STATE.RECORDING);
      setStatus(null);
      setElapsed(0);
    } else if (blob && state === STATE.RECORDING) {
      setState(STATE.REVIEW);
    }
  }, [rec, blob, state]);

  useEffect(() => {
    if (err) {
      setStatus({ t: "error", m: err });
      setState(STATE.IDLE);
    }
  }, [err]);

  useEffect(() => {
    if (state !== STATE.RECORDING) return;
    const i = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(i);
  }, [state]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    const onT = () => setTime(a.currentTime);
    const onD = () => setDur(a.duration);
    const onE = () => setPlaying(false);

    a.addEventListener("timeupdate", onT);
    a.addEventListener("durationchange", onD);
    a.addEventListener("ended", onE);

    return () => {
      a.removeEventListener("timeupdate", onT);
      a.removeEventListener("durationchange", onD);
      a.removeEventListener("ended", onE);
    };
  }, [url]);

  const onRecord = () => (rec ? stop() : start());
  const onReset = () => {
    reset();
    setState(STATE.IDLE);
    setStatus(null);
  };

  const onSubmit = async () => {
    if (!blob) return;
    setState(STATE.VERIFYING);
    setStatus({ t: "loading", m: "Uploading audio..." });

    try {
      // Convert blob to File
      const file = new File([blob], "recording.webm", { type: "audio/webm" });

      // 1. Upload file
      const uploadRes = await ImageFileUpload(
        "biostarapp/user/file-upload",
        file,
        "file",
      );
      setStatus({ t: "loading", m: "Submitting recording..." });

      // 2. Submit upload response to submit API
      let fileUrl = "";
      if (Array.isArray(uploadRes?.data) && uploadRes.data.length > 0) {
        fileUrl = uploadRes.data[0];
      } else if (typeof uploadRes?.data === "string") {
        fileUrl = uploadRes.data;
      } else {
        fileUrl = uploadRes?.fileUrl || uploadRes?.url || uploadRes?.path;
      }

      const submitRes = await postRequest(
        "biostarapp/user/client/upload/voxplayer/file",
        {
          file: fileUrl,
          voicePlayerId: voicePlayerId,
        },
      );

      setState(STATE.SUCCESS);
      setStatus({ t: "success", m: submitRes?.msg || "Recording submitted!" });

      setTimeout(() => {
        reset();
        setStmt(STATEMENTS[Math.floor(Math.random() * STATEMENTS.length)]);
        setState(STATE.IDLE);
        setStatus(null);
      }, 3000);
    } catch (err) {
      setState(STATE.FAILED);
      setStatus({
        t: "error",
        m:
          err?.response?.data?.msg ||
          err?.message ||
          "Submission failed. Try again.",
      });
    }
  };

  const toggle = () => {
    if (playing) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setPlaying(!playing);
  };

  const canRec = state === STATE.IDLE || state === STATE.FAILED;
  const canSub = state === STATE.REVIEW;
  const proc = state === STATE.VERIFYING || state === STATE.SUCCESS;

  // useEffect(() => {
  //   if (!userData) return;

  //   const uid = userData.embedUid || userData.id;

  //   fetch(`biostarapp/user/voice-player-detail/${uid}`);
  // }, [userData]);

  // console.log("USER DATA:", userData);

  return (
    <div className={`voxplayer-container ${mounted ? "mounted" : ""}`}>
      {/* Background orbs */}
      <div className="background-orbs">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="orb-grid" />
      </div>

      <div className="voxplayer-content">
        {/* Header */}
        <header className="voxplayer-header">
          <div className="logo-container">
            <div className="logo-icon">
              {[36, 46, 56].map((s, i) => (
                <span key={i} className={`ring-ring ring-${i}`} />
              ))}
              <svg
                className="logo-svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#60a5fa"
                strokeWidth="1.5"
              >
                <path d="M12 2a3 3 0 00-3 3v7a3 3 0 006 0V5a3 3 0 00-3-3z" />
                <path d="M19 10v2a7 7 0 01-14 0v-2" />
                <path d="M12 19v3" />
              </svg>
            </div>
            <h1 className="logo-text">VoxPlayer</h1>
            <span className="logo-badge">Pro</span>
          </div>
          <p className="logo-subtitle">Next-Gen Voice Capture</p>
        </header>

        {/* Statement Card */}
        <div className="statement-wrapper">
          <div className="statement-header">
            <span className="pulse-dot" />
            <span className="read-text">Read aloud</span>
          </div>
          <div
            className={`statement-card ${state === STATE.RECORDING ? "recording-active" : ""}`}
          >
            <p className="statement-text">{stmt}</p>
          </div>
        </div>

        {/* Recording Indicator */}
        {state === STATE.RECORDING && (
          <div className="recording-indicator">
            <div className="recording-box">
              <span className="recording-dot" />
              <span className="recording-text">Recording</span>
              <span className="recording-timer">{formatTime(elapsed)}</span>
              <div className="wave-animation">
                {[6, 14, 20, 10, 16].map((h, i) => (
                  <div key={i} className={`wave-bar wave-${i}`} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Playback */}
        {url && state !== STATE.RECORDING && state !== STATE.SUCCESS && (
          <div className="playback-container">
            <audio ref={audioRef} src={url} />
            <div className="playback-controls">
              <button onClick={toggle} className="playback-toggle">
                {playing ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
              <div className="playback-progress">
                <div className="progress-track">
                  <div
                    className="progress-bar"
                    style={{ width: `${dur ? (time / dur) * 100 : 0}%` }}
                  />
                </div>
                <div className="progress-time">
                  <span>{formatTime(Math.floor(time))}</span>
                  <span>{formatTime(Math.floor(dur || 0))}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status */}
        {status && (
          <div className={`status-message status-${status.t}`}>
            {status.t === "loading" && <div className="status-spinner" />}
            {status.t === "success" && (
              <svg
                className="status-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#34d399"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
            )}
            {status.t === "error" && (
              <svg
                className="status-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f43f5e"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
            )}
            <span className="status-text">{status.m}</span>
          </div>
        )}

        {/* Buttons */}
        <div className="action-buttons">
          {(canRec || state === STATE.RECORDING) && (
            <button
              onClick={onRecord}
              disabled={proc}
              className={`record-button ${state === STATE.RECORDING ? "recording" : ""} ${proc ? "disabled" : ""}`}
            >
              {state === STATE.RECORDING ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                  Stop Recording
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                    <circle cx="12" cy="12" r="5" />
                  </svg>
                  {state === STATE.FAILED ? "Try Again" : "Begin Recording"}
                </>
              )}
            </button>
          )}

          {canSub && (
            <button
              onClick={onSubmit}
              disabled={proc}
              className={`submit-button ${proc ? "disabled" : ""}`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
              >
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
              Submit Recording
            </button>
          )}

          {state === STATE.REVIEW && (
            <button
              onClick={onReset}
              disabled={proc}
              className={`reset-button ${proc ? "disabled" : ""}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8M3 3v5h5" />
              </svg>
              Start Over
            </button>
          )}
        </div>

        {/* Hint */}
        {state === STATE.IDLE && (
          <div className="hint-box">
            <svg
              className="hint-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#60a5fa"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <p className="hint-text">
              Read the statement clearly. Your voice will be verified before
              submission.
            </p>
          </div>
        )}

        {/* Footer */}
        <footer className="voxplayer-footer">
          <div className="footer-status">
            <span className="status-dot" />
            Ready to record
          </div>
          <span className="footer-copyright">Powered by Biostar</span>
        </footer>
      </div>
    </div>
  );
}
